import React from "react";
import { Global, Collection } from "@sourceflow-uk/sourceflow-sdk";
import { SourceFlowHead } from "@sourceflow-uk/sourceflow-head";
import metaObject from "@/.sourceflow/metadata.json";
import sourceflowEnums from "@/sourceflow.enums";
import SimpleFormWithColumn from "components/SimpleFormWithColumn";
import SubBanner from "@/components/SubBanner";
import FooterWave from "@/public/site-assets/svg/footer-wave-2.svg";
import Wave3 from "@/public/site-assets/svg/wave-3.svg";

import ImageWithText from "components/ImageWithText";
import MultiValues from "components/MultiValues";
import TextWithVideo from "components/TextWithVideo";

import { router } from "@/routes";
import { useLocale } from "@/hooks/useLocale";
/**
 *
 * @param global
 * @param meta
 * @returns {JSX.Element}
 * @constructor
 */
export default function TheNationalCollege({
    global,
    meta,
    national_college_point_values,
    popular_cpd_topics
}) {
    const locale = useLocale();

    return (
        <>
            <SourceFlowHead
                metaObject={metaObject}
                title={meta.title}
                description={meta.description}
                canonical={meta.canonical}
            />
            <section className="the-national-college-page">
                <SubBanner
                    global={global}
                    pathPrefix={`cpd.the_national_college.page.sub_banner`}
                    heading={`The National College`}
                    content={<p>Our exclusive training and development partnership</p>}
                    breadcrumb_title={<><span>CPD</span><span className="active">The National College</span></>}
                    wave_color={`light-grey`}
                    tnc_logo={`/images/placeholders/tnc-logo.png`}
                    className={`pt-4 cpd-the-national-college-page`}
                    waveIcon={false}
                />
                <div className="position-relative pt-5 bg-light-white">
                    <TextWithVideo
                        global={global}
                        pathPrefix={`cpd.the_national_college.page.text_with_video`}
                        content={
                            <>
                                <p>The National College is an award-winning online CPD platform with more than 2,500 courses, webinars and resources that can be accessed on the go.</p>
                                <p>Designed with convenience in mind, The National College has been built to help teachers, support staff and school leaders keep on top of the latest statutory guidance, improve their knowledge and stay ahead of developments in pedagogy.</p>
                                <p>As part of our continued dedication to excellence within schools, we work in partnership with The National College to offer our candidates a complimentary subscription to the platform. They can then pursue their development at their own pace and in their own time.</p>
                            </>
                        }
                        heading={`What is The National College?`}
                        video={"/videos/final-nc-video.mp4"}
                        className="pb-5 position-relative z-index-1"
                    />
                    <div className="overlay-img-1">
                        <Wave3 />
                    </div>
                </div>
                <MultiValues
                    global={global}
                    pathPrefix={`cpd.the_national_college.page.multi_values`}
                    post={national_college_point_values}
                />
                <div className="rotate footer bg-light-grey">
                    <FooterWave />
                </div>
                <ImageWithText
                    global={global}
                    pathPrefix={`cpd.the_national_college.page.image_with_text`}
                    heading={`Popular CPD Topics`}
                    lead_content={
                        <>
                            <p>With so many topics to explore on The National College, it can be difficult to know where to begin! So to guide their learning, our candidates receive a custom learning pathway when they sign up to the platform through us.</p>
                        </>
                    }
                    content={
                        <>
                            <p><b>Here are some of the most popular CPD categories:</b></p>

                        </>
                    }
                    content_html={true}
                    post={popular_cpd_topics}
                    className="py-5 style-1"
                    imageURL={"/images/placeholders/popular-cpd-placeholder.png"}
                />
                <div className="rotate footer light-gray-wave">
                    <FooterWave />
                </div>
                <div className="bg-white pt-5"></div>
                <SimpleFormWithColumn
                    global={global}
                    pathPrefix={`cpd.the_national_college.page.simple_form_with_column`}
                    heading={`Get in touch`}
                    content={<p>To make a booking or discuss our services further, get in touch with your local Tradewind office. A consultant will be assigned to assist you with all of your education recruitment needs and they’ll be in touch as soon as possible.</p>}
                    formId={`${sourceflowEnums.forms.contact_us}`}
                    imageURL={``}
                    className={`bg-white py-5`}
                />
                <div className="bg-white pt-5"></div>
                <div className="footer">
                    <FooterWave />
                </div>
            </section>
        </>
    );
}

/**
 *
 * @returns {Promise<{props: {meta: {description: string, canonical: string, title: string}, jobs: *, global}}>}
 */
export async function getStaticProps() {
    const global = (await Global()).toJson();
    const national_college_point_values = (await Collection(`national_college_point_values`)).getItems()
    const popular_cpd_topics = (await Collection(`popular_cpd_topics`)).getItems()

    const routerKey = "cpd.the_national_college";

    return {
        props: {
            global,
            meta: {
                title: "The National College | CPD | Tradewind Subs",
                description: `${sourceflowEnums.seo.genericDescription}`,
                // canonical: sourceflowEnums.seo.baseURL,
                canonical: `${sourceflowEnums.seo.baseURL}${router[routerKey]["en"]}`,
                // alternate: { link: router[routerKey]["en"], lang: "en" },
            },
            national_college_point_values,
            popular_cpd_topics
        },
    };
}

// -- Reference -- //
// https://github.com/sourceflow-uk/components/pkgs/npm/sourceflow-head
// https://github.com/sourceflow-uk/components/pkgs/npm/sourceflowtext
// https://github.com/sourceflow-uk/components/pkgs/npm/job-search